import type { AxiosError } from 'axios';
import axios from 'axios';
import dayjs from 'dayjs';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { Org } from '../../../enum';
import { AppCode } from '../../../enum';
import { APIStatus } from '../../../enum';
import { getCSVFilename } from '../../../helpers/utils.helper';
import type {
  BaseDashboardParams,
  ChatLengthBin,
  Dow,
  MissedChat,
  NameValue,
  OtherSubject,
  SubjectTotal,
} from '../../../models/dashboard';
import type { LineChartData } from '../../../models/echarts';
import type { ErrorMessage } from '../../../models/error';
import type { AsyncThunkConfig } from '../../../models/slice';
import { type FilteredInfo, type RequestParams, SortedInfo, type TableChange } from '../../../models/table';
import { RaygunErrorHandlerService } from '../../../service/raygun.service';
import type { AlertDashboardBody } from '../alerts/alertSlice';
import type { Organization } from '../orgs/orgsSlice';
import { dateTimeFilterFormat, getChatDailyCSV } from './program-dashboard.helper';

const { logError } = RaygunErrorHandlerService();

export type DashboardParams = BaseDashboardParams & {
  for_leps: boolean;
  for_service_providers: boolean;
};

type ModalTypes = MissedChat | OtherSubject;

export type Search = {
  search_dow: Dow[];
  empty_search_results: number | null;
  empty_search_sessions: number | null;
  unique_guests_with_empty_searches: number | null;
};

export type ChatActivity = {
  data: LineChartData[];
  csv: string;
  filename: string;
};

export type GuestReach = { ios?: number; android?: number; web?: number };

export type GuestMetrics = {
  new_guests: GuestReach | null;
  total_guests: GuestReach | null;
};

export type DashboardData = {
  total_chats: number | null;
  average_chat_length: number | null;
  chat_lengths: ChatLengthBin[];
  unique_guests_chatting: number | null;
  percentage_repeat_chatters: number | null;
  issues: {
    reports_of_abuse: number | null;
    reports_of_emergency: number | null;
    blocked_guests: number | null;
    missed_chat_count: number | null;
  };
  feedback: {
    chat_helpful: NameValue[];
    chat_end_reasons: NameValue[];
    chat_subjects: SubjectTotal[];
    other_chat_subjects: SubjectTotal[];
    chat_referred_guest: NameValue[];
    guest_feelings: SubjectTotal[];
    guest_feelings_reporting_rate: SubjectTotal[];
  };
  advocate_dow: Dow[];
};

type Subject = {
  id: string;
  subject: string;
  alias: string;
};

type ProgramDashboardSliceType = {
  dashboardParams: DashboardParams;
  dashboardData: DashboardData;
  search: Search;
  searchApiStatus: APIStatus;
  chatActivity: ChatActivity;
  chatActivityApiStatus: APIStatus;
  guestMetrics: GuestMetrics;
  guestMetricsApiStatus: APIStatus;
  requestModalParam: RequestParams<ModalTypes>;
  dashboardApiStatus: APIStatus;
  csv_exports: {
    feedback_csv: string;
    advocate_hours_csv: string;
    search_csv: string;
  };
  modal: DASHBOARD_MODAL;
  filtersInfo: string;
  subjects: Subject[];
};

export const DASHBOARD_MODAL = {
  IDLE: 'idle',
  ADVOCATE_DAY_AND_TIME: 'advocate_day_and_time',
  SEARCH_DAY_AND_TIME: 'search_day_and_time',
  OTHER_SUBJECTS: 'other_subjects',
  CHAT_LENGTHS: 'chat_lengths',
  EMPTY_SEARCHES: 'empty_searches',
  ALERTS_REGISTERED: 'alerts_registered',
  CHAT_INITIATION: 'chat_initiation',
  BQ_ACTIVE_GUESTS: 'bq_active_guests',
} as const;

export type DASHBOARD_MODAL = (typeof DASHBOARD_MODAL)[keyof typeof DASHBOARD_MODAL];

const initialState: ProgramDashboardSliceType = {
  dashboardParams: {
    app_code: AppCode.TalkingStick,
    start_date: dayjs().subtract(1, 'month'),
    end_date: dayjs(),
    selected_org_code: 'allOrgs',
    for_leps: true,
    for_service_providers: true,
  },
  requestModalParam: {
    pagination: {
      current: 1,
      pageSize: 50,
      defaultPageSize: 50,
    },
    sortedInfo: new SortedInfo<ModalTypes>('total', 'descend'),
    filteredInfo: {},
  },
  dashboardData: {
    total_chats: null,
    average_chat_length: null,
    chat_lengths: [],
    unique_guests_chatting: null,
    percentage_repeat_chatters: null,
    issues: {
      reports_of_abuse: null,
      reports_of_emergency: null,
      blocked_guests: null,
      missed_chat_count: null,
    },
    feedback: {
      chat_helpful: [],
      chat_end_reasons: [],
      chat_subjects: [],
      other_chat_subjects: [],
      chat_referred_guest: [],
      guest_feelings: [],
      guest_feelings_reporting_rate: [],
    },
    advocate_dow: [],
  },
  guestMetrics: {
    new_guests: null,
    total_guests: null,
  },
  chatActivity: {
    data: [],
    csv: '',
    filename: '',
  },
  search: {
    search_dow: [],
    empty_search_results: null,
    empty_search_sessions: null,
    unique_guests_with_empty_searches: null,
  },
  csv_exports: {
    feedback_csv: '',
    advocate_hours_csv: '',
    search_csv: '',
  },
  modal: DASHBOARD_MODAL.IDLE,
  dashboardApiStatus: APIStatus.IDLE,
  searchApiStatus: APIStatus.IDLE,
  chatActivityApiStatus: APIStatus.IDLE,
  guestMetricsApiStatus: APIStatus.IDLE,
  filtersInfo: '',
  subjects: [],
};

export type DashboardBody = {
  selected_org_code: Org | null;
  start_date: string;
  end_date: string;
  app_code: AppCode;
  for_leps: boolean;
  for_service_providers: boolean;
};

export const getFiltersInfo = (body: AlertDashboardBody | DashboardBody, orgs: Organization[]) => {
  let text = `${dayjs(body.start_date).format('YYYY-MM-DD')} - ${dayjs(body.end_date).format('YYYY-MM-DD')}`;
  const selectedOrgName = orgs.find((o) => o.code === body.selected_org_code)?.name;
  if (selectedOrgName) {
    text += ` ${selectedOrgName}`;
  }

  const dashboardBody = body as DashboardBody;
  if (dashboardBody.for_leps && dashboardBody.for_service_providers) {
    // do nothing
  } else if (dashboardBody.for_leps) {
    text += ' PAs';
  } else if (dashboardBody.for_service_providers) {
    text += ' SPs';
  }

  return text;
};

// oxlint-disable-next-line typescript/no-invalid-void-type
export const loadDashboard = createAsyncThunk<void, undefined, AsyncThunkConfig>(
  'programDashboard/loadDashboard',
  async (_, thunkAPI) => {
    try {
      const promises: { abort: VoidFunction }[] = [];
      thunkAPI.signal.addEventListener('abort', () => {
        promises.forEach((p) => p.abort());
      });
      const dashboardParams = thunkAPI.getState().programDashboardSlice.dashboardParams;
      const body: DashboardBody = {
        app_code: dashboardParams.app_code,
        start_date: dashboardParams.start_date.startOf('day').format(dateTimeFilterFormat),
        end_date: dashboardParams.end_date.endOf('day').format(dateTimeFilterFormat),
        selected_org_code: dashboardParams.selected_org_code === 'allOrgs' ? null : dashboardParams.selected_org_code,
        for_leps: dashboardParams.for_leps,
        for_service_providers: dashboardParams.for_service_providers,
      };
      promises.push(thunkAPI.dispatch(getMainDashboard(body)));
      promises.push(thunkAPI.dispatch(getSearchStats(body)));
      promises.push(thunkAPI.dispatch(getChatActivity(body)));
      promises.push(thunkAPI.dispatch(getGuestMetrics(body)));
    } catch (e) {
      logError(e, ['programDashboardSlice', 'loadDashboard']);
    }
  },
);

export const getMainDashboard = createAsyncThunk<DashboardData, DashboardBody, AsyncThunkConfig>(
  'programDashboard/getMainDashboard',
  async (body, thunkAPI) => {
    try {
      const source = axios.CancelToken.source();
      thunkAPI.signal.addEventListener('abort', () => {
        source.cancel();
      });
      const response = (await axios.post('v2_program_dashboard', body, {
        cancelToken: source.token,
      })) as DashboardData;
      const tsOrgs = thunkAPI.getState().orgsSlice.orgsForAdminDic.ts;
      thunkAPI.dispatch(setFiltersInfo(getFiltersInfo(body, tsOrgs)));
      return response;
    } catch (e) {
      const error = e as AxiosError<ErrorMessage>;
      if (error.name !== 'CanceledError') {
        logError(error, ['programDashboardSlice', 'getMainDashboard']);
      }
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  },
);

export const exportFeedback = createAsyncThunk<string, undefined, AsyncThunkConfig>(
  'programDashboard/exportFeedback',
  async (_, thunkAPI) => {
    try {
      const dashboardParams = thunkAPI.getState().programDashboardSlice.dashboardParams;
      const body: DashboardBody = {
        app_code: dashboardParams.app_code,
        start_date: dashboardParams.start_date.startOf('day').format(dateTimeFilterFormat),
        end_date: dashboardParams.end_date.endOf('day').format(dateTimeFilterFormat),
        selected_org_code: dashboardParams.selected_org_code === 'allOrgs' ? null : dashboardParams.selected_org_code,
        for_leps: dashboardParams.for_leps,
        for_service_providers: dashboardParams.for_service_providers,
      };
      const response = (await axios.post('v2_feedback_export', body)) as string;
      return response;
    } catch (e) {
      const error = e as AxiosError<ErrorMessage>;
      logError(error, ['programDashboardSlice', 'exportFeedback']);
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  },
);

export const exportSearches = createAsyncThunk<string, undefined, AsyncThunkConfig>(
  'programDashboard/exportSearches',
  async (_, thunkAPI) => {
    try {
      const dashboardParams = thunkAPI.getState().programDashboardSlice.dashboardParams;
      const body: DashboardBody = {
        app_code: dashboardParams.app_code,
        start_date: dashboardParams.start_date.startOf('day').format(dateTimeFilterFormat),
        end_date: dashboardParams.end_date.endOf('day').format(dateTimeFilterFormat),
        selected_org_code: dashboardParams.selected_org_code === 'allOrgs' ? null : dashboardParams.selected_org_code,
        for_leps: dashboardParams.for_leps,
        for_service_providers: dashboardParams.for_service_providers,
      };
      const response = (await axios.post('v2_searches_export', body)) as string;
      return response;
    } catch (e) {
      const error = e as AxiosError<ErrorMessage>;
      logError(error, ['programDashboardSlice', 'exportSearches']);
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  },
);

export const exportAdvocateHours = createAsyncThunk<string, undefined, AsyncThunkConfig>(
  'programDashboard/exportAdvocateHours',
  async (_, thunkAPI) => {
    try {
      const dashboardParams = thunkAPI.getState().programDashboardSlice.dashboardParams;
      const body: DashboardBody = {
        app_code: dashboardParams.app_code,
        start_date: dashboardParams.start_date.startOf('day').format(dateTimeFilterFormat),
        end_date: dashboardParams.end_date.endOf('day').format(dateTimeFilterFormat),
        selected_org_code: dashboardParams.selected_org_code === 'allOrgs' ? null : dashboardParams.selected_org_code,
        for_leps: dashboardParams.for_leps,
        for_service_providers: dashboardParams.for_service_providers,
      };
      const response = (await axios.post('v2_advocate_hours_export', body)) as string;
      return response;
    } catch (e) {
      const error = e as AxiosError<ErrorMessage>;
      logError(error, ['programDashboardSlice', 'exportAdvocateHours']);
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  },
);

export const getSearchStats = createAsyncThunk<Search, DashboardBody, AsyncThunkConfig>(
  'programDashboard/getSearchStats',
  async (body, thunkAPI) => {
    try {
      const source = axios.CancelToken.source();
      thunkAPI.signal.addEventListener('abort', () => {
        source.cancel();
      });
      const response = (await axios.post('v2_program_dashboard_search_stats', body, {
        cancelToken: source.token,
      })) as Search;
      return response ?? initialState.search;
    } catch (e) {
      const error = e as AxiosError<ErrorMessage>;
      if (error.name !== 'CanceledError') {
        logError(error, ['programDashboardSlice', 'getSearchStats']);
      }
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  },
);

export const getChatActivity = createAsyncThunk<ChatActivity, DashboardBody, AsyncThunkConfig>(
  'programDashboard/getChatActivity',
  async (body, thunkAPI) => {
    try {
      const source = axios.CancelToken.source();
      thunkAPI.signal.addEventListener('abort', () => {
        source.cancel();
      });
      const response = (await axios.post('v2_program_dashboard_chat_activity', body, {
        cancelToken: source.token,
      })) as LineChartData[];
      const data = response ?? [];
      const tsOrgs = thunkAPI.getState().orgsSlice.orgsForAdminDic.ts;
      return {
        data,
        csv: getChatDailyCSV(data),
        filename: getCSVFilename(`Chat activity (${getFiltersInfo(body, tsOrgs)})`),
      };
    } catch (e) {
      const error = e as AxiosError<ErrorMessage>;
      if (error.name !== 'CanceledError') {
        logError(error, ['programDashboardSlice', 'getChatActivity']);
      }
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  },
);

export const getGuestMetrics = createAsyncThunk<GuestMetrics, DashboardBody, AsyncThunkConfig>(
  'programDashboard/getGuestMetrics',
  async (body, thunkAPI) => {
    try {
      const source = axios.CancelToken.source();
      thunkAPI.signal.addEventListener('abort', () => {
        source.cancel();
      });
      const response = (await axios.post('v2_program_dashboard_guest_metrics', body, {
        cancelToken: source.token,
      })) as GuestMetrics;
      return (
        response ?? {
          new_guests: null,
          total_guests: null,
        }
      );
    } catch (e) {
      const error = e as AxiosError<ErrorMessage>;
      if (error.name !== 'CanceledError') {
        logError(error, ['programDashboardSlice', 'getGuestMetrics']);
      }
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  },
);

export const getSubjects = createAsyncThunk<Subject[], AppCode, AsyncThunkConfig>(
  'programDashboard/getSubjects',
  async (app_code, thunkAPI) => {
    try {
      const response = (await axios.post('v0_get_subjects', { app_code })) as Subject[];
      return response ?? [];
    } catch (e) {
      logError(e, ['programDashboardSlice', 'getSubjects']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const reassignSubject = createAsyncThunk<
  number,
  { other_subject: string; new_subject_id: string },
  AsyncThunkConfig
>('programDashboard/reassignSubject', async (body, thunkAPI) => {
  try {
    const response = (await axios.post('v0_reassign_subjects', body)) as number;
    return response;
  } catch (e) {
    logError(e, ['programDashboardSlice', 'reassignSubject']);
    return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
  }
});

export const programDashboardSlice = createSlice({
  name: 'programDashboard',
  initialState,
  reducers: {
    handleParamsChange: (state, action: PayloadAction<Partial<DashboardParams>>) => {
      state.dashboardParams = { ...state.dashboardParams, ...action.payload };
    },
    showDashboardModal: (state, action: PayloadAction<DASHBOARD_MODAL>) => {
      state.modal = action.payload;
    },
    closeDashboardModal: (state) => {
      state.modal = DASHBOARD_MODAL.IDLE;
    },
    handleModalTableChange: (state, action: PayloadAction<Partial<TableChange<ModalTypes>>>) => {
      const pagination = action.payload.pagination;
      const filters = action.payload.filters as FilteredInfo;
      const sorter = action.payload.sorter as SortedInfo<ModalTypes>;
      state.requestModalParam.pagination = pagination || initialState.requestModalParam.pagination;
      state.requestModalParam.sortedInfo = sorter || initialState.requestModalParam.sortedInfo;
      state.requestModalParam.filteredInfo = filters || initialState.requestModalParam.filteredInfo;
    },
    clearModalFilter: (state) => {
      state.requestModalParam.pagination = initialState.requestModalParam.pagination;
      state.requestModalParam.sortedInfo = initialState.requestModalParam.sortedInfo;
      state.requestModalParam.filteredInfo = initialState.requestModalParam.filteredInfo;
    },
    setFiltersInfo: (state, action: PayloadAction<string>) => {
      state.filtersInfo = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(exportFeedback.fulfilled, (state, action) => {
        state.csv_exports.feedback_csv = action.payload;
      })
      .addCase(exportAdvocateHours.fulfilled, (state, action) => {
        state.csv_exports.advocate_hours_csv = action.payload;
      })
      .addCase(exportSearches.fulfilled, (state, action) => {
        state.csv_exports.search_csv = action.payload;
      })
      .addCase(getMainDashboard.pending, (state, _action) => {
        state.filtersInfo = '';
        state.dashboardApiStatus = APIStatus.PENDING;
      })
      .addCase(getMainDashboard.fulfilled, (state, action) => {
        state.dashboardData = action.payload;
        state.dashboardApiStatus = APIStatus.FULFILLED;
      })
      .addCase(getMainDashboard.rejected, (state, action) => {
        if (action.error.name !== 'AbortError') {
          state.dashboardApiStatus = APIStatus.ERROR;
        }
      })
      .addCase(getSearchStats.pending, (state, _action) => {
        state.searchApiStatus = APIStatus.PENDING;
      })
      .addCase(getSearchStats.fulfilled, (state, action) => {
        state.search = action.payload;
        state.searchApiStatus = APIStatus.FULFILLED;
      })
      .addCase(getSearchStats.rejected, (state, action) => {
        if (action.error.name !== 'AbortError') {
          state.searchApiStatus = APIStatus.ERROR;
        }
      })
      .addCase(getChatActivity.pending, (state, _action) => {
        state.chatActivityApiStatus = APIStatus.PENDING;
      })
      .addCase(getChatActivity.fulfilled, (state, action) => {
        state.chatActivity = action.payload;
        state.chatActivityApiStatus = APIStatus.FULFILLED;
      })
      .addCase(getChatActivity.rejected, (state, action) => {
        if (action.error.name !== 'AbortError') {
          state.chatActivityApiStatus = APIStatus.ERROR;
        }
      })
      .addCase(getGuestMetrics.pending, (state, _action) => {
        state.guestMetricsApiStatus = APIStatus.PENDING;
      })
      .addCase(getGuestMetrics.fulfilled, (state, action) => {
        state.guestMetrics = action.payload;
        state.guestMetricsApiStatus = APIStatus.FULFILLED;
      })
      .addCase(getGuestMetrics.rejected, (state, action) => {
        if (action.error.name !== 'AbortError') {
          state.guestMetricsApiStatus = APIStatus.ERROR;
        }
      })
      .addCase(getSubjects.fulfilled, (state, action) => {
        state.subjects = action.payload;
      });
  },
});

export const {
  handleParamsChange,
  showDashboardModal,
  closeDashboardModal,
  handleModalTableChange,
  clearModalFilter,
  setFiltersInfo,
} = programDashboardSlice.actions;
